/**
 * @voltxampere/web — Pre-engine circuit validation (Sprint 25 R2 lane 2)
 *
 * Closes TS-008 from the Sprint 25 R1 triage. Catches semantic
 * topology problems BEFORE the circuit reaches the engine worker so
 * the user sees a meaningful toast instead of "DC sonucu yok" + 0
 * console + 0 toast (the silent-fail signature documented in
 * `tools/triage/reports/E2E_BROWSER_REPORT.md`).
 *
 * Engine zero-diff disipline: ABSOLUTE. This validator lives in
 * `apps/web/src/store/`; no `packages/engine/src/` mutation. The
 * existing `@voltxampere/schema` `validateCircuitInvariants` covers
 * link integrity (Net.id references, single-ground, name uniqueness)
 * — this module complements it with circuit-topology semantics that
 * are user-meaningful but cannot be expressed in the schema.
 *
 * Rules (Sprint 25 R2 lane 2):
 *   1. `missing-ground`         — error: no ground component AND no
 *                                  net with `isGround: true`. The
 *                                  engine cannot solve without a
 *                                  voltage reference.
 *   2. `dangling-pin`           — error: a component terminal is the
 *                                  only user of its net. Engine
 *                                  receives a pin "floating" with no
 *                                  external connection.
 *   3. `floating-net`           — error: a net has zero terminal/wire
 *                                  users. Schema parse catches the
 *                                  "wire references unknown net" case;
 *                                  this catches "net declared but
 *                                  unused".
 *   4. `voltage-source-short`   — error: a voltage / pulse / AC source
 *                                  has both terminals on the same net.
 *                                  Engine throws Singular matrix; we
 *                                  catch it earlier with a clearer
 *                                  message.
 *   5. `isolated-subgraph`      — warning: 2+ disconnected component
 *                                  groups. Sometimes intentional
 *                                  (parallel sub-circuits sharing
 *                                  ground) so we surface as warning
 *                                  rather than blocking error.
 *
 * Returns a structured result so callers can map each error to a
 * specific i18n key + offender id pair.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { Circuit, ComponentInstance } from "@voltxampere/schema";

export type ValidationSeverity = "error" | "warning";

export type ValidationCode =
  | "missing-ground"
  | "dangling-pin"
  | "floating-net"
  | "voltage-source-short"
  | "isolated-subgraph";

export interface ValidationIssue {
  readonly code: ValidationCode;
  readonly severity: ValidationSeverity;
  /** i18n key for the human message (e.g. `"validation.error.dangling-pin"`). */
  readonly messageKey: string;
  /**
   * Template params for `t(messageKey, params)`. String-only because
   * the i18n runtime substitutes `{name}` literals.
   */
  readonly messageParams: Readonly<Record<string, string>>;
  /** Offending component id, if applicable. */
  readonly componentId?: string;
  /** Offending net id, if applicable. */
  readonly netId?: string;
  /** Offending terminal id, if applicable (only for dangling-pin). */
  readonly terminalId?: string;
}

export interface ValidationResult {
  /** True when there are no `severity: "error"` issues. */
  readonly valid: boolean;
  readonly errors: readonly ValidationIssue[];
  readonly warnings: readonly ValidationIssue[];
}

/**
 * Component types the engine treats as a controllable / forced
 * voltage source. Two-terminal short of any of these = singular MNA
 * matrix.
 */
const VOLTAGE_SOURCE_TYPES: ReadonlySet<string> = new Set([
  "voltage-source-dc",
  "voltage-source-ac",
  "voltage-source-pulse",
  "ac-source",
  "pulse-source",
  "pwl-source",
  // Sprint 25 R2 lane 4 will add the "voltage-source" alias UI side;
  // include it here so the validator stays correct after that lands.
  "voltage-source",
]);

/**
 * Component types that anchor the circuit's reference voltage. Either
 * one of these OR an `isGround: true` net satisfies the missing-ground
 * rule.
 */
const GROUND_COMPONENT_TYPES: ReadonlySet<string> = new Set(["ground", "gnd"]);

/**
 * Validate a Circuit for the 5 rules above. Pure function — no
 * side effects, no signal mutation. Callers (runDc/Transient/Ac
 * runners) inspect the result and surface errors via the toast
 * stack.
 */
export function validateCircuit(circuit: Circuit): ValidationResult {
  const errors: ValidationIssue[] = [];
  const warnings: ValidationIssue[] = [];

  // -------- Net user census --------
  // For every netId, count its terminal users + wire users + ground
  // anchor users. Used by dangling-pin, floating-net, isolated-subgraph.
  const netUserCount = new Map<string, number>();
  const netToComponents = new Map<string, Set<string>>();
  for (const net of circuit.nets) {
    netUserCount.set(net.id, 0);
    netToComponents.set(net.id, new Set<string>());
  }

  for (const comp of circuit.components) {
    for (const term of comp.terminals) {
      const prior = netUserCount.get(term.netId) ?? 0;
      netUserCount.set(term.netId, prior + 1);
      const componentSet = netToComponents.get(term.netId);
      if (componentSet !== undefined) {
        componentSet.add(comp.id);
      }
    }
  }

  for (const wire of circuit.wires) {
    const prior = netUserCount.get(wire.netId) ?? 0;
    // A wire with N segments contributes a single connection to its
    // net (it's the net itself, not a "user" per se), but treat each
    // wire as one user so a net with wire-only consumers ranks as
    // weakly-connected. The dangling-pin rule still uses terminal
    // count separately.
    netUserCount.set(wire.netId, prior + 1);
  }

  // -------- Rule 1: missing-ground --------
  const hasGroundNet = circuit.nets.some((n) => n.isGround);
  const hasGroundComponent = circuit.components.some((c) => GROUND_COMPONENT_TYPES.has(c.type));
  if (!hasGroundNet && !hasGroundComponent) {
    errors.push({
      code: "missing-ground",
      severity: "error",
      messageKey: "validation.error.missing-ground",
      messageParams: {},
    });
  }

  // -------- Rule 2: dangling-pin --------
  // A terminal whose net has no other terminal/wire user. Skip terminals
  // on ground nets (they implicitly anchor) and skip ground components
  // entirely (ground's "single-pin to ground net" is canonical, not
  // dangling).
  const groundNetIds = new Set(circuit.nets.filter((n) => n.isGround).map((n) => n.id));
  for (const comp of circuit.components) {
    if (GROUND_COMPONENT_TYPES.has(comp.type)) continue;
    for (const term of comp.terminals) {
      if (groundNetIds.has(term.netId)) continue;
      const users = netUserCount.get(term.netId) ?? 0;
      if (users < 2) {
        errors.push({
          code: "dangling-pin",
          severity: "error",
          messageKey: "validation.error.dangling-pin",
          messageParams: {
            component: comp.label ?? comp.id,
            terminal: term.terminalId,
          },
          componentId: comp.id,
          netId: term.netId,
          terminalId: term.terminalId,
        });
      }
    }
  }

  // -------- Rule 3: floating-net --------
  // Net declared but not referenced by any terminal or wire. Skip
  // ground nets (they may exist as a reference even with no explicit
  // wire — ground component handles them).
  for (const net of circuit.nets) {
    if (net.isGround) continue;
    const users = netUserCount.get(net.id) ?? 0;
    if (users === 0) {
      errors.push({
        code: "floating-net",
        severity: "error",
        messageKey: "validation.error.floating-net",
        messageParams: { name: net.name },
        netId: net.id,
      });
    }
  }

  // -------- Rule 4: voltage-source-short --------
  for (const comp of circuit.components) {
    if (!VOLTAGE_SOURCE_TYPES.has(comp.type)) continue;
    if (comp.terminals.length !== 2) continue;
    const [a, b] = comp.terminals;
    if (a !== undefined && a.netId === b?.netId) {
      errors.push({
        code: "voltage-source-short",
        severity: "error",
        messageKey: "validation.error.voltage-source-short",
        messageParams: { component: comp.label ?? comp.id },
        componentId: comp.id,
      });
    }
  }

  // -------- Rule 5: isolated-subgraph (warning) --------
  // Build a component-level adjacency graph: components are nodes,
  // shared net = undirected edge. Run union-find to count connected
  // components. >1 component = isolated subgraph warning.
  if (circuit.components.length >= 2) {
    const componentCount = countConnectedSubgraphs(circuit.components);
    if (componentCount > 1) {
      warnings.push({
        code: "isolated-subgraph",
        severity: "warning",
        messageKey: "validation.warning.isolated-subgraph",
        messageParams: { count: String(componentCount) },
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Union-find connected-component count over the component-graph. Two
 * components are considered adjacent when they share at least one
 * non-ground net via their terminal lists.
 */
function countConnectedSubgraphs(components: readonly ComponentInstance[]): number {
  const n = components.length;
  if (n === 0) return 0;

  const parent = new Array<number>(n);
  for (let i = 0; i < n; i += 1) parent[i] = i;

  function find(i: number): number {
    let cursor = i;
    while (parent[cursor] !== cursor) {
      const p = parent[cursor];
      if (p === undefined) return cursor;
      parent[cursor] = parent[p] ?? p;
      cursor = parent[cursor] ?? cursor;
    }
    return cursor;
  }

  function union(a: number, b: number): void {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  }

  // netId → first component index that touched it; subsequent touches
  // union with that root.
  const netRoot = new Map<string, number>();
  for (let i = 0; i < n; i += 1) {
    const comp = components[i];
    /* v8 ignore next */
    if (comp === undefined) continue;
    for (const term of comp.terminals) {
      const root = netRoot.get(term.netId);
      if (root === undefined) {
        netRoot.set(term.netId, i);
      } else {
        union(root, i);
      }
    }
  }

  // Count distinct roots.
  const roots = new Set<number>();
  for (let i = 0; i < n; i += 1) roots.add(find(i));
  return roots.size;
}
